import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
  IconButton,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LogoutIcon from '@mui/icons-material/Logout';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import Logo from '../Assets/Images/Logo.png';
import handleLogout from '../Services/signOut';
import s3DownloadFile from '../Services/s3DownloadFile';
import { getAuth } from 'firebase/auth';
import { logFactory } from '../Services/Log.factory';
import { getUserCustomClaims } from '../Services/getUserClaims';

const handleDownload = async (value) => {
  try {
    // Verificar se o usuário é admin antes de permitir download
    const claims = await getUserCustomClaims();
    if (!claims.admin) {
      alert(
        'Acesso negado. Apenas administradores podem fazer download de arquivos.'
      );
      return;
    }

    // Tentar registrar o log, mas não bloquear o download se falhar
    try {
      const auth = getAuth();
      const object = {
        Bucket: process.env.REACT_APP_AWS_BUCKET,
        Date: new Date(),
        Key: value,
        UserEmail: auth.currentUser?.email || 'unknown',
      };
      logFactory(object).catch((logError) => {
        console.warn('Erro ao registrar log de download:', logError);
        // Não bloquear o download por causa do erro de log
      });
    } catch (logError) {
      console.warn('Erro ao preparar log de download:', logError);
    }

    // Abrir download independentemente do log
    window.open(s3DownloadFile(value));
  } catch (error) {
    console.error('Erro ao verificar permissões:', error);
    alert('Erro ao verificar permissões. Tente novamente.');
  }
};

export default function AuditTable({
  data,
  onLoadMore,
  hasMore,
  loading,
  onRefresh,
  onSearch,
}) {
  const [search, setSearch] = React.useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
  const [visibleColumns, setVisibleColumns] = React.useState({
    download: true,
    Attendant: true,
    destination: true,
    key: true,
    lastModified: true,
    callHour: true,
    hour: true,
  });

  // Verificar se o usuário é admin/superAdmin
  React.useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      try {
        const claims = await getUserCustomClaims();
        const adminStatus = claims.admin === true;
        const superAdminStatus = claims.superAdmin === true;

        // Só atualiza o estado se o componente ainda estiver montado
        if (isMounted) {
          setIsAdmin(adminStatus);
          setIsSuperAdmin(superAdminStatus);

          // Se não for admin, desabilitar coluna de download
          if (!adminStatus) {
            setVisibleColumns((prev) => ({ ...prev, download: false }));
          }
        }
      } catch (error) {
        console.error('Erro ao verificar claims:', error);
        // Em caso de erro, desabilitar download por segurança
        if (isMounted) {
          setVisibleColumns((prev) => ({ ...prev, download: false }));
        }
      }
    };

    checkAdminStatus();

    // Cleanup function para evitar memory leak
    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenColumns = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumns = () => setColumnsMenuAnchor(null);
  const toggleColumn = (col) =>
    setVisibleColumns((prev) => ({ ...prev, [col]: !prev[col] }));

  const filtered = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      [
        row.Attendant,
        row.destination,
        row.key,
        row.lastModified,
        row.callHour,
        row.hour,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  const exportCsv = () => {
    const headers = [
      'Origem',
      'Destino',
      'Nome do Arquivo',
      'Data',
      'Hora da Ligação',
      'Hora no Server',
    ];
    const rows = filtered.map((r) => [
      r.Attendant ?? '',
      r.destination ?? '',
      r.key ?? '',
      r.lastModified ?? '',
      r.callHour ?? '',
      r.hour ?? '',
    ]);
    const buildCsv = [headers, ...rows]
      .map((line) =>
        line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const blob = new Blob([buildCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `auditoria_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <img
          src={Logo}
          alt='Logo getinfo Sistemas'
          style={{ width: '266px', height: '86px' }}
        />
        <Stack direction='row' spacing={1} alignItems='center'>
          <SearchIcon />
          <TextField
            size='small'
            placeholder='Buscar...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onSearch) onSearch(search);
            }}
          />
          <Button
            variant='contained'
            size='small'
            onClick={() => onSearch && onSearch(search)}
          >
            Buscar
          </Button>
          <Button
            variant='text'
            size='small'
            onClick={() => {
              setSearch('');
              if (onSearch) onSearch('');
            }}
          >
            Limpar busca
          </Button>
          <Button
            variant='outlined'
            size='small'
            onClick={onRefresh}
            disabled={loading}
          >
            Atualizar
          </Button>
          {isSuperAdmin && (
            <Tooltip title='Auditar'>
              <IconButton onClick={() => { window.location.href = '/admin'; }}>
                <AdminIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Exportar CSV'>
            <span>
              <IconButton onClick={exportCsv} disabled={filtered.length === 0}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Exibir colunas'>
            <IconButton onClick={handleOpenColumns}>
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Sair'>
            <IconButton
              onClick={async () => {
                await handleLogout();
                window.location.href = '/';
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={handleCloseColumns}
      >
        {[
          ...(isAdmin ? [{ key: 'download', label: 'Download (Admin)' }] : []),
          { key: 'Attendant', label: 'Origem' },
          { key: 'destination', label: 'Destino' },
          { key: 'key', label: 'Nome do Arquivo' },
          { key: 'lastModified', label: 'Data' },
          { key: 'callHour', label: 'Hora da Ligação' },
          { key: 'hour', label: 'Hora no Server' },
        ].map((c) => (
          <MenuItem key={c.key} onClick={() => toggleColumn(c.key)}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!visibleColumns[c.key]}
                  onChange={() => toggleColumn(c.key)}
                />
              }
              label={c.label}
            />
          </MenuItem>
        ))}
      </Menu>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {isAdmin && visibleColumns.download && (
                <TableCell>Download</TableCell>
              )}
              {visibleColumns.Attendant && <TableCell>Origem</TableCell>}
              {visibleColumns.destination && <TableCell>Destino</TableCell>}
              {visibleColumns.key && <TableCell>Nome do Arquivo</TableCell>}
              {visibleColumns.lastModified && <TableCell>Data</TableCell>}
              {visibleColumns.callHour && (
                <TableCell>Hora da Ligação</TableCell>
              )}
              {visibleColumns.hour && <TableCell>Hora no Server</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(filtered) && filtered.length > 0 ? (
              filtered.map((row) => (
                <TableRow key={row.key} hover>
                  {isAdmin && visibleColumns.download && (
                    <TableCell>
                      <IconButton
                        aria-label='save'
                        onClick={() => handleDownload(row.key)}
                      >
                        <SaveIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  )}
                  {visibleColumns.Attendant && (
                    <TableCell>{row.Attendant}</TableCell>
                  )}
                  {visibleColumns.destination && (
                    <TableCell>{row.destination}</TableCell>
                  )}
                  {visibleColumns.key && (
                    <TableCell>
                      <Tooltip title={row.key}>
                        <span>{row.key}</span>
                      </Tooltip>
                    </TableCell>
                  )}
                  {visibleColumns.lastModified && (
                    <TableCell>{row.lastModified}</TableCell>
                  )}
                  {visibleColumns.callHour && (
                    <TableCell>{row.callHour}</TableCell>
                  )}
                  {visibleColumns.hour && <TableCell>{row.hour}</TableCell>}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  <Typography variant='body2'>
                    Nenhum registro encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        {hasMore && (
          <Button variant='outlined' onClick={onLoadMore} disabled={loading}>
            {loading ? 'Carregando...' : 'Carregar mais'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
